import { Capacitor } from "@capacitor/core";
import { Geolocation, type PermissionStatus, type Position } from "@capacitor/geolocation";
import type { NearPosition } from "../types/venues";

// Nearby venues first (founder, 2026-10-29). The first tap on the Venue box
// in Edit profile asks for the phone's location — the system prompt, BEFORE
// the search screen and its keyboard open — then the search opens at once.
// The position is fetched in the background (approximate, cached, under our
// own timeout) and rides along with the searches once it is in; searches
// before that lean on the talent's country, exactly as without it.
//
//   allowed already      → no prompt; a fresh rough position each open
//   never asked          → one prompt, then the search (never waiting on a fix)
//   no / restricted / unavailable (no Play Services, an error, a timeout, a
//   browser without geolocation) → never asked again, nothing shown
//
// The position is rounded to 2 decimals (about 1 km) and held in memory for
// this session only — never stored, never logged (lib/venueSearch.js on the
// server uses it for its one Google call). The only thing kept is the talent's
// "no", so we never ask twice.
//
// App: @capacitor/geolocation — in the iPhone app the webview's own
// navigator.geolocation would show a second, WebKit prompt on top of the
// app's. Website: navigator.geolocation, the browser's prompt; a "no" there
// is final. Plugin calls are chained, never awaited (a Capacitor plugin proxy
// fakes `then`).

/** Longest the search waits for the talent to answer the prompt: the
    phone's own (modal) prompt, and a browser's, which on a computer is a
    bubble that can go unnoticed. */
export const ANSWER_CAP_MS = 10_000;
export const WEB_ANSWER_CAP_MS = 5_000;
/** Longest a position fix may take (7.x ignores its own timeout on iOS). */
export const FIX_TIMEOUT_MS = 10_000;
/** A fix this recent is good enough for "near you". */
const MAX_AGE_MS = 10 * 60_000;
/** Holds only the talent's "no" — never a position. */
export const SAID_NO_KEY = "rq_venue_location_no";

let near: NearPosition | null = null;

/** The rough position for venue searches, once there is one this session. */
export function nearbyPosition(): NearPosition | null {
  return near;
}

const round = (n: number) => Math.round(n * 100) / 100 || 0; // || 0: never -0

function keep(p: Pick<Position, "coords"> | GeolocationPosition | null | undefined): void {
  const lat = p?.coords?.latitude;
  const lng = p?.coords?.longitude;
  if (typeof lat !== "number" || typeof lng !== "number" || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
  near = { lat: round(lat), lng: round(lng) };
}

function saidNo(): boolean {
  try {
    return localStorage.getItem(SAID_NO_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberNo(): void {
  try {
    localStorage.setItem(SAID_NO_KEY, "1");
  } catch {
    /* private mode: the OS / browser still remembers the "no" */
  }
}

/** `p`, or `fallback` after `ms` — whichever comes first. Never rejects. */
function within<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

type Answer = "granted" | "ask" | "no";

// ── the phone apps ──
// Android 12+ may grant approximate only: coarseLocation granted, location not.
const isGranted = (s: PermissionStatus | null | undefined) => s?.location === "granted" || s?.coarseLocation === "granted";

function nativeAnswer(): Promise<Answer> {
  // Rejects when location services are off (or the plugin is missing): not
  // now, and nothing stored, so it can still ask once if they come back on.
  return Geolocation.checkPermissions().then(
    (s): Answer => (isGranted(s) ? "granted" : s?.location === "prompt" && s?.coarseLocation === "prompt" ? "ask" : "no"),
    (): Answer => "no",
  );
}

function fetchNative(): void {
  within<Position | null>(Geolocation.getCurrentPosition({ enableHighAccuracy: false, maximumAge: MAX_AGE_MS, timeout: FIX_TIMEOUT_MS }), FIX_TIMEOUT_MS, null).then(keep);
}

function askNative(): Promise<void> {
  // "location" (fine + coarse) rather than "coarseLocation": on Android 11 and
  // older the 7.x plugin's position call needs both, and asking for coarse
  // only would bring a second prompt there. Android 12+ still lets the
  // talent pick Approximate; iOS treats both the same (approximate by
  // default, NSLocationDefaultAccuracyReduced).
  return Geolocation.requestPermissions({ permissions: ["location"] }).then(
    (s) => {
      if (isGranted(s)) fetchNative();
      else rememberNo();
    },
    () => undefined,
  );
}

// ── the website ──
function webAnswer(): Promise<Answer> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return Promise.resolve("no");
  const perms = navigator.permissions;
  if (!perms?.query) return Promise.resolve("ask"); // no Permissions API: the first call tells
  return perms.query({ name: "geolocation" as PermissionName }).then(
    (s): Answer => (s.state === "granted" ? "granted" : s.state === "prompt" ? "ask" : "no"),
    (): Answer => "ask",
  );
}

const WEB_OPTIONS: PositionOptions = { enableHighAccuracy: false, maximumAge: MAX_AGE_MS, timeout: FIX_TIMEOUT_MS };

function fetchWeb(): void {
  navigator.geolocation.getCurrentPosition(keep, () => undefined, WEB_OPTIONS);
}

function askWeb(): Promise<void> {
  return new Promise((resolve) => {
    // The search opens on the answer itself (the permission flips), not on
    // the fix that follows it.
    navigator.permissions
      ?.query({ name: "geolocation" as PermissionName })
      .then((s) => {
        s.onchange = () => {
          if (s.state !== "prompt") resolve();
        };
      })
      .catch(() => undefined);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        keep(p);
        resolve();
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) rememberNo();
        resolve();
      },
      WEB_OPTIONS,
    );
  });
}

/**
 * The Venue box was tapped. Resolves when the search may open: at once, or —
 * the first time, while the location question is open — once the talent has
 * answered (at most ANSWER_CAP_MS, WEB_ANSWER_CAP_MS on the website). Never
 * waits for a position fix; never rejects.
 */
export function prepareVenueLocation(): Promise<void> {
  const native = Capacitor.isNativePlatform();
  const answer: Promise<Answer> = saidNo() ? Promise.resolve("no") : native ? nativeAnswer() : webAnswer();
  const ready = answer.then((a) => {
    if (a === "granted") (native ? fetchNative : fetchWeb)();
    else if (a === "ask") return native ? askNative() : askWeb();
    return undefined;
  });
  return within<void>(ready, native ? ANSWER_CAP_MS : WEB_ANSWER_CAP_MS, undefined);
}

/** Tests only: forget this session's position. */
export function resetVenueLocationForTest(): void {
  near = null;
}
